import { FormatIconType } from '../../components/core/FormatIcon';

export interface IAppFile {
  url: string;
  fileName: string;
  fileSize: number;
  file?: File;
  extension?: FormatIconType;
}

export type ValidateFn = (files: IAppFile[]) => Promise<undefined | string>;
export enum ConverterStatus {
  ERROR,
  UPLOAD_ERROR,
  EMPTY,
  INIT,
  PROCESSING,
  PROCESSED,
  CONVERTING,
  CONVERTED,
  DOWNLOADING,
  DOWNLOADED,
}

export interface IProgress {
  percent: number;
  processed: number;
  total: number;
}

export interface IConverterOptions {
  onProgress?: (progress: IProgress) => void;
}

export interface IConverter<O extends IConverterOptions, R extends IAppFile | Blob> {
  setSources: (
    sources: IAppFile[] | ((prev: IAppFile[]) => IAppFile[]),
    pageName?: string,
    medium?: string,
  ) => void;
  setOptions: (options: O | ((prev: O) => O)) => void;

  removeAt: (index: number) => void;
  removeAll: () => void;
  convert: () => void;
  download: () => void;
  reset: () => void;
  is: (...statuses: ConverterStatus[]) => boolean;

  status: ConverterStatus;
  options: O;
  progress?: IProgress;

  sources: IAppFile[];
  results: R[];

  actionsDisabled: boolean;

  error: string;
  setError: (error: string) => void;
}

export type IConverterHook<
  O extends IConverterOptions,
  R extends IAppFile | Blob,
  Converter = IConverter<O, R>,
> = (defaultOptions: O | (() => O)) => Converter;

export type IConverterBase<O extends IConverterOptions, R extends IAppFile> = Omit<
  IConverter<O, R>,
  'convert'
> & {
  setStatus: (status: ConverterStatus) => void;
  setProgress: (status?: IProgress) => void;
  setResults: (results: Array<R>) => void;
  setError: (e: string, status?: ConverterStatus) => void;
  setSourcesWithoutValidation: IConverter<O, R>['setSources'];
};
